'use client'

import { motion } from 'motion/react'

/**
 * Hero-scoped orange radial glow. Keep this CSS-only so the landing hero
 * cannot be covered by an oversized shader canvas during first paint.
 *
 * Host requirements: the parent must be `position: relative` and
 * `overflow: hidden` (and ideally `isolate`) so the glow + dither stay
 * clipped to the hero band.
 */

const backgroundVariants = {
  hidden: {
    opacity: 0,
    '--gradient-size-x': '0%',
    '--gradient-size-y': '0%',
    '--stop-1': '0%',
    '--stop-2': '0%',
    '--stop-3': '0%',
    '--stop-4': '0%',
  },
  visible: {
    opacity: 1,
    '--gradient-size-x': '180%',
    '--gradient-size-y': '220%',
    '--stop-1': '12%',
    '--stop-2': '30%',
    '--stop-3': '45%',
    '--stop-4': '75%',
    transition: {
      duration: 1.2,
      ease: [0.4, 0.0, 0.2, 1] as const,
      opacity: {
        duration: 0.4,
        ease: 'easeOut' as const,
      },
      '--gradient-size-x': {
        duration: 1.0,
        delay: 0.1,
        ease: [0.4, 0.0, 0.2, 1] as const,
      },
      '--gradient-size-y': {
        duration: 1.0,
        delay: 0.1,
        ease: [0.4, 0.0, 0.2, 1] as const,
      },
      '--stop-1': {
        duration: 0.9,
        delay: 0.15,
        ease: 'easeOut' as const,
      },
      '--stop-2': {
        duration: 0.9,
        delay: 0.2,
        ease: 'easeOut' as const,
      },
      '--stop-3': {
        duration: 0.9,
        delay: 0.25,
        ease: 'easeOut' as const,
      },
      '--stop-4': {
        duration: 0.9,
        delay: 0.3,
        ease: 'easeOut' as const,
      },
    },
  },
}

export function HeroGlow() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
    >
      {/* Radial glow, translated down a lot so it sits behind the composer
          and demo frame rather than washing out the headline. */}
      <div className="absolute inset-0 translate-y-[25%]">
        <motion.div
          className="create-page-bg absolute inset-0"
          initial="hidden"
          animate="visible"
          variants={backgroundVariants}
          style={{
            // @ts-expect-error - CSS custom properties
            '--gradient-size-x': '180%',
            '--gradient-size-y': '220%',
            '--stop-1': '12%',
            '--stop-2': '30%',
            '--stop-3': '45%',
            '--stop-4': '75%',
          }}
        />
      </div>
    </div>
  )
}
